import type { RootState } from '@core';
import { schemasAdapter, seatsAdapter } from './slice';
import { createSelector } from '@reduxjs/toolkit';
import { buildSeatPreviewUrl, type BuildPreviewParams } from '../utils/seatPreview';

/**
 * Базовый селектор для доступа к состоянию модуля seatSelection
 */
const seatSelectionStateSelectors = createSelector(
  (state: RootState) => state,
  state => state.seatSelection
);

export const renderRulesSelector = createSelector(
  seatSelectionStateSelectors,
  state => state.renderRules ?? null
);

export const seatPhotoRulesSelector = createSelector(
  renderRulesSelector,
  renderRules => renderRules?.seatPhotoRules ?? null
);

export const seatMapRulesSelector = createSelector(
  renderRulesSelector,
  renderRules => renderRules?.seatMapRules ?? null
);

// Получаем базовые селекторы из адаптеров
const schemasSelectorsAdapter = schemasAdapter.getSelectors<RootState>(
  state => state.seatSelection.schemas
);

const seatsSelectorsAdapter = seatsAdapter.getSelectors<RootState>(
  state => state.seatSelection.seats
);

// Основные селекторы из адаптеров - они уже оптимизированы внутри адаптера
/**
 * Получить все схемы мест (координаты и базовая информация)
 */
export const schemasSelectors = schemasSelectorsAdapter.selectAll;

/**
 * Получить все места с подробной информацией (цены и т. д.)
 */
export const seatsSelectors = seatsSelectorsAdapter.selectAll;

// Кастомные селекторы с мемоизацией
/**
 * Получить выбранные места
 */
export const selectedSeatsSelector = createSelector(
  seatSelectionStateSelectors,
  state => state?.selectedSeats ?? []
);

/**
 * Получить ID выбранных мест
 */
export const selectedSeatIdsSelector = createSelector(selectedSeatsSelector, selectedSeats =>
  selectedSeats.map(seat => seat.seatId)
);

export const seatPreviewBuilderSelector = createSelector(seatPhotoRulesSelector, seatPhotoRules =>
  seatPhotoRules?.length
    ? (params: BuildPreviewParams) => buildSeatPreviewUrl(params, seatPhotoRules)
    : null
);

export const seatPreviewAvailableSelector = createSelector(seatPhotoRulesSelector, seatPhotoRules =>
  Boolean(seatPhotoRules?.length)
);

/**
 * Получение сообщения об ошибке, если есть
 */
export const errorSelectors = createSelector(
  seatSelectionStateSelectors,
  state => state?.error ?? null
);

/**
 * Получение данных URL параметров (showIds и eventsIds)
 */
export const urlParamsDataSelectors = createSelector(
  seatSelectionStateSelectors,
  state => state?.urlParamsData
);

export const uniquePricesSelectors = createSelector(seatsSelectors, seats =>
  Array.from(new Set(seats?.map(seat => seat.price))).sort((a, b) => a - b)
);

export const isSeatResourcesLoadingSelector = (state: RootState) =>
  state.seatSelection.isSeatResourcesLoading;

export const hasSeatResourcesForSelector = (
  state: RootState,
  { showId, eventId }: { showId?: number; eventId?: number }
) => {
  const { loadedSeatResourcesFor } = state.seatSelection;

  return (
    loadedSeatResourcesFor !== null &&
    loadedSeatResourcesFor.showId === showId &&
    loadedSeatResourcesFor.eventId === eventId
  );
};
